import { useEffect, useRef } from 'react';

interface UseAppForegroundOptions {
  /** Minimum background duration (ms) before triggering callback */
  minBackgroundMs: number;
  /** Called when app returns to foreground after sufficient background time */
  onForeground: (backgroundDurationMs: number) => void;
  /** Whether the hook is active */
  enabled?: boolean;
}

/**
 * Sprint 17e: Detects when the app returns to the foreground after being
 * backgrounded for at least `minBackgroundMs` milliseconds.
 * Uses the Page Visibility API (document.visibilitychange).
 */
export function useAppForeground({ minBackgroundMs, onForeground, enabled = true }: UseAppForegroundOptions) {
  const hiddenAtRef = useRef<number | null>(null);
  const onForegroundRef = useRef(onForeground);
  onForegroundRef.current = onForeground;

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAtRef.current !== null) {
        const backgroundDurationMs = Date.now() - hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (backgroundDurationMs >= minBackgroundMs) {
          onForegroundRef.current(backgroundDurationMs);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled, minBackgroundMs]);
}
